import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import StatsDashboard from '../components/stats/StatsDashboard';
import CheckInButton from '../components/attendance/CheckInButton';
import AttendanceWarning from '../components/attendance/AttendanceWarning';
import ConsecutiveWorkWarning from '../components/schedule/ConsecutiveWorkWarning';
import WeeklyHoursWarning from '../components/schedule/WeeklyHoursWarning';
import { SlideUpPage } from '../components/common/PageTransition';
import { getAttendanceStats } from '../utils/attendanceApi';
import { AttendanceStats } from '../types/attendance';

const HomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AttendanceStats | null>(null);

  // 출근 통계 로드
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await getAttendanceStats();
      setStats(response.data);
    } catch (error) {
      console.error('통계 로드 실패:', error);
    }
  };

  return (
    <SlideUpPage>
    <main className="container mx-auto px-4 py-12 pb-0 overflow-x-hidden">
      {/* 비활성 계정 안내 카드 */}
      {user && !user.isActive && (
        <div className="mb-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-xl p-5 shadow-lg">
          <div className="flex items-start gap-3">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1">
              <h3 className="text-lg font-bold text-yellow-800 mb-2">
                계정이 비활성화되었습니다
              </h3>
              <p className="text-sm text-yellow-700 mb-3 leading-relaxed">
                근무확인서만 조회 가능합니다.<br />
                다른 기능 사용이 필요하면 관리자에게 문의하세요.
              </p>
              <button
                onClick={() => navigate('/scheduler')}
                className="w-full px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold rounded-lg hover:shadow-lg transition-all"
              >
                📄 근무확인서 보기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 활성 계정만 보이는 컨텐츠 */}
      {user && user.isActive && (
        <>
          {/* 출근 버튼 */}
          <CheckInButton />

          {/* 만근수당 트래커 (제일 위) */}
          <section className="mb-8">
            <AttendanceWarning 
              late={stats?.late || 0} 
              earlyLeave={stats?.earlyLeave || 0} 
              absent={stats?.absent || 0} 
            />
          </section>

          {/* 노동법 체크 섹션 */}
          <section className="mb-8 space-y-3">
            {/* 연속 근무일 (긴 직사각형) */}
            <ConsecutiveWorkWarning consecutiveDays={stats?.consecutiveDays || 0} />
            
            {/* 이번 주 근무시간 (긴 직사각형) */}
            <WeeklyHoursWarning weeklyHours={stats?.weeklyHours || 0} />
          </section>

          {/* 실시간 통계 대시보드 */}
          <StatsDashboard />
        </>
      )}

      {/* 프로젝트 현황 (컴팩트) */}
      <details className="mb-8">
        <summary className="cursor-pointer bg-gradient-to-r from-accent/5 to-primary/5 rounded-xl p-3 hover:shadow-lg transition-all flex items-center justify-between list-none">
          <div className="flex items-center gap-2 flex-1">
            <span className="text-lg">🚀</span>
            <h3 className="text-xs font-bold text-accent">Project AZOCSII 로드맵</h3>
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
              Phase 1-2 완료!
            </div>
          </div>
          <span className="text-xl text-primary transition-transform duration-200 hover:scale-125">▼</span>
        </summary>
        
        <section className="grid md:grid-cols-3 gap-8">
        {/* Phase 1-2: Daily Joy */}
        <div className="card-azocsii">
          <div className="text-4xl mb-4">📅</div>
          <h3 className="text-2xl font-bold text-primary mb-3">Daily Joy & Guardian Shield</h3>
          <p className="text-gray-700 mb-4">
            일상과 권리를 지키는 스마트 솔루션
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>✓ AI 스마트 스케줄러</li>
            <li>✓ 아조씨 로또 (공정한 추첨)</li>
            <li>✓ 셀프 스왑 마켓</li>
            <li>✓ 수당 수호자 트래커</li>
            <li>✓ 스마트 증거 수집기</li>
          </ul>
        </div>

        {/* Project AZOCSII - 프로젝트 진행 상황 */}
        <div className="card-azocsii border-4 border-accent relative overflow-hidden">
          {/* 배경 패턴 */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-accent/10 to-transparent rounded-full blur-2xl"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="text-4xl">🚀</div>
                <h3 className="text-2xl font-bold text-accent">Project AZOCSII</h3>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                Phase 1-2 완료!
              </div>
            </div>
            
            <p className="text-gray-700 mb-6 text-sm">
              트렌디한 PM 로드맵 · 실시간 진행 상황 · 시대를 앞서가는 UX
            </p>

            <div className="space-y-5">
              {/* Phase 1-2: Daily Joy - 완료 */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border-2 border-green-300">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">✅</span>
                    <span className="font-bold text-green-700">Phase 1-2: Daily Joy</span>
                  </div>
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">100%</span>
                </div>
                <div className="w-full bg-green-200 rounded-full h-2 mb-2">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full" style={{ width: '100%' }}></div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-green-700 mt-3">
                  <div className="flex items-center gap-1">
                    <span>✓</span>
                    <span>스마트 스케줄러</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>✓</span>
                    <span>아조씨 로또</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>✓</span>
                    <span>셀프 스왑 마켓</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>✓</span>
                    <span>Context API</span>
                  </div>
                </div>
              </div>

              {/* Phase 3: Guardian Shield - 진행 예정 */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border-2 border-blue-300">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🛡️</span>
                    <span className="font-bold text-blue-700">Phase 3: Guardian Shield</span>
                  </div>
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">Next</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full" style={{ width: '0%' }}></div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-600 mt-3">
                  <div className="flex items-center gap-1">
                    <span>○</span>
                    <span>수당 트래커</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>○</span>
                    <span>증거 수집기</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>○</span>
                    <span>법률 자문 AI</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>○</span>
                    <span>권리 알림</span>
                  </div>
                </div>
              </div>

              {/* Phase 4-5: Global & Wellness - 계획 */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border-2 border-purple-300">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🌍</span>
                    <span className="font-bold text-purple-700">Phase 4-5: Global & Wellness</span>
                  </div>
                  <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold">Plan</span>
                </div>
                <div className="w-full bg-purple-200 rounded-full h-2 mb-2">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full" style={{ width: '0%' }}></div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-purple-600 mt-3">
                  <div className="flex items-center gap-1">
                    <span>○</span>
                    <span>글로벌 지도</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>○</span>
                    <span>AI 웰니스</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>○</span>
                    <span>연대 캠페인</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>○</span>
                    <span>커리어 빌더</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 전체 진행률 */}
            <div className="mt-6 bg-gradient-to-r from-accent to-secondary text-white rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold">전체 프로젝트 진행률</span>
                <span className="text-2xl font-black">33%</span>
              </div>
              <div className="w-full bg-white/30 rounded-full h-3">
                <div className="bg-white h-3 rounded-full shadow-lg" style={{ width: '33%' }}></div>
              </div>
              <p className="text-xs mt-2 opacity-90">Phase 1-2 완료 · 2/6 단계 달성 🎉</p>
            </div>
          </div>
        </div>

        {/* Phase 4-5: Global Oasis */}
        <div className="card-azocsii">
          <div className="text-4xl mb-4">🌍</div>
          <h3 className="text-2xl font-bold text-primary mb-3">Global Oasis & AI Wellness</h3>
          <p className="text-gray-700 mb-4">
            글로벌 연대와 웰니스
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li>✓ 3D 글로벌 고충 지도</li>
            <li>✓ AI 건강 노트 트래커</li>
            <li>✓ Daily mood tracker</li>
            <li>✓ 커리어 프로젝트 빌더</li>
            <li>✓ 연대 캠페인</li>
          </ul>
        </div>
      </section>
      </details>

      {/* CSI 재정의 섹션 */}
      <section className="relative bg-white rounded-2xl p-10 md:p-12 shadow-lg text-center mb-16 overflow-hidden">
        {/* 무지개 테두리 애니메이션 */}
        <div className="absolute inset-0 rounded-2xl p-[3px] bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 via-purple-500 to-pink-500 animate-border-flow">
          <div className="absolute inset-[3px] bg-white rounded-2xl"></div>
        </div>
        
        {/* 내용 */}
        <div className="relative z-10">
          <h3 className="text-2xl md:text-3xl font-bold mb-6">
            <span className="text-red-500">진</span>
            <span className="text-orange-500">정</span>
            <span className="text-yellow-500">한</span>
            {' '}
            <span className="text-green-500">C</span>
            <span className="text-blue-500">S</span>
            <span className="text-purple-500">I</span>
            <span className="text-gray-400">의</span>
            {' '}
            <span className="text-purple-600">재정의</span>
          </h3>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-8 mb-6 px-2">
            <div className="text-gray-400 line-through">
              <p className="text-sm font-semibold mb-1">기존</p>
              <p className="text-lg md:text-xl">Customer Satisfaction Index</p>
            </div>
            <div className="text-3xl md:text-4xl">→</div>
            <div className="text-gray-700">
              <p className="text-sm font-semibold mb-1">새롭게</p>
              <p className="text-xl md:text-2xl lg:text-3xl font-bold leading-relaxed">
                <span className="text-3xl md:text-4xl lg:text-5xl font-black bg-gradient-to-r from-green-400 via-emerald-500 to-teal-500 bg-clip-text text-transparent">C</span>
                <span className="text-green-600">ounselor</span>
                {' '}
                <span className="text-3xl md:text-4xl lg:text-5xl font-black bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-500 bg-clip-text text-transparent">S</span>
                <span className="text-blue-600">olidarity</span>
                {' '}
                <span className="text-3xl md:text-4xl lg:text-5xl font-black bg-gradient-to-r from-purple-400 via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">I</span>
                <span className="text-purple-600">nitiative</span>
              </p>
            </div>
          </div>
          <p className="text-xs md:text-sm text-gray-600 italic px-4">
            "기업이 강요하던 고객 만족 지수를<br />
            상담사 연대 이니셔티브로 재정의합니다"
          </p>
        </div>

        {/* CSS 애니메이션 추가 */}
        <style>{`
          @keyframes border-flow {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          .animate-border-flow {
            background-size: 300% 300%;
            animation: border-flow 3s ease infinite;
          }
        `}</style>
      </section>

      {/* 환영 섹션 - 압축 버전 */}
      <section className="text-center mb-12">
        <div className="relative overflow-hidden rounded-3xl p-6 mb-6 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400">
          {/* 배경 패턴 */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-white rounded-full blur-3xl"></div>
          </div>
          
          {/* 메인 콘텐츠 */}
          <div className="relative z-10">
            {/* 돈까스 아조씨 이미지 - 축소 */}
            <div className="mb-3">
              <div className="w-24 h-24 mx-auto rounded-full overflow-hidden shadow-2xl hover:scale-110 transition-transform duration-300 border-4 border-white">
                <img 
                  src="/images/azocsii-mascot.jpg" 
                  alt="돈까스 아조씨 - AZOCSII Mascot"
                  className="w-full h-full object-cover object-center scale-150"
                  style={{ objectPosition: '50% 35%' }}
                />
              </div>
            </div>
            
            <h2 className="text-2xl md:text-3xl font-black text-white mb-3 tracking-tight">
              Welcome to AZOCSII
            </h2>
            
            {/* 서브타이틀 */}
            <div className="inline-block bg-black bg-opacity-50 backdrop-blur-sm rounded-2xl px-4 py-2 mb-4">
              <p className="text-sm text-white font-bold flex items-center justify-center gap-2">
                <span>🎧</span>
                <span>돈까스 아조씨</span>
                <span>🎵</span>
              </p>
            </div>

            {/* AZOCSII 약자 무지개 컬러 - 2x3 그리드 */}
            <div className="grid grid-cols-2 gap-2 max-w-xs mx-auto mb-4">
              <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl p-2 hover:bg-opacity-30 transition-all">
                <p className="text-2xl font-black text-red-500">A</p>
                <p className="text-xs text-white">Authentic</p>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl p-2 hover:bg-opacity-30 transition-all">
                <p className="text-2xl font-black text-orange-500">Z</p>
                <p className="text-xs text-white">Zeal</p>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl p-2 hover:bg-opacity-30 transition-all">
                <p className="text-2xl font-black text-yellow-400">O</p>
                <p className="text-xs text-white">Oasis</p>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl p-2 hover:bg-opacity-30 transition-all">
                <p className="text-2xl font-black text-green-500">C</p>
                <p className="text-xs text-white">Counselor</p>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl p-2 hover:bg-opacity-30 transition-all">
                <p className="text-2xl font-black text-blue-500">S</p>
                <p className="text-xs text-white">Solidarity</p>
              </div>
              <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl p-2 hover:bg-opacity-30 transition-all">
                <p className="text-2xl font-black text-indigo-500">I</p>
                <p className="text-xs text-white">Initiative</p>
              </div>
            </div>

            {/* 캠페인 메시지 */}
            <div className="inline-block bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 rounded-full px-6 py-2 text-sm font-black shadow-2xl hover:scale-105 transition-transform">
              <div className="flex items-center gap-1.5">
                <span>🌍</span>
                <span>A to Z for Every Humanist</span>
                <span>✨</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="text-center pb-0">
        <Link to="/scheduler" className="btn-azocsii text-xl inline-block">
          스케줄러 시작하기 🚀
        </Link>
        <p className="mt-4 mb-0 text-gray-600 px-4">
          전 세계 휴머니스트들이여, 연대하십시오.<br />
          당신의 오아시스가 여기 있습니다.
        </p>
      </section>

      {/* CSS: details 화살표 회전 애니메이션 */}
      <style>{`
        details[open] summary span:last-child {
          transform: rotate(180deg);
        }
        details summary {
          list-style: none;
        }
        details summary::-webkit-details-marker {
          display: none;
        }
      `}</style>
    </main>
    </SlideUpPage>
  );
};

export default HomePage;
